import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { GroupOffersBy, TOffer, TPartner } from 'types/offers-temporary';
import { Button, Input } from 'rsuite';
import Particles from "react-tsparticles";
import { Container, Engine } from "tsparticles-engine";
import { loadFull } from "tsparticles";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import AllOffersList from './all/all-offers-list';
import OffersList from './offers/offers-list';
import AddSectionToListModal from './modals/add-section-to-list-modal';
import SectionsManager from './sections/sections-manager';
import './_list.scss';
import SplitLayout from './split/split-layout';
import { PanelService } from '../../../network/panel/network';
import type { ISectionsList, IAppOffersSection } from '@bprotsyk/aso-core/lib/offers/list';
import type { IOffersSection } from '@bprotsyk/aso-core/lib/offers/section';

interface OfferListContainerProps {
  onSectionsListChange?: (newSectionsList: ISectionsList) => void;
}

interface ListItem {
  id: number;
  name: string;
  sectionsList: ISectionsList;
}

const OfferListContainer: React.FC<OfferListContainerProps> = ({ onSectionsListChange }) => {
  const [selectedOffers, setSelectedOffers] = useState<TOffer[]>([]);
  const [partners, setPartners] = useState<Record<string, TPartner>>({});
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<number>(1);
  const [draggedOffer, setDraggedOffer] = useState<TOffer | null>(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [lastExpandedSection, setLastExpandedSection] = useState<number>(1);
  const [sectionsList, setSectionsList] = useState<ISectionsList>({
    id: 1,
    name: 'Sections List 1',
    content: [
      {
        sectionId: 1,
        offerStatesOrdered: {}
      },
      {
        sectionId: 2,
        offerStatesOrdered: {}
      }
    ]
  });

  // New state for hierarchical view
  const [lists, setLists] = useState<ListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingListName, setIsEditingListName] = useState(false);
  const [editingListName, setEditingListName] = useState('');
  const [activeTab, setActiveTab] = useState<'offerwall' | 'direct' | 'sections'>('offerwall');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState<ListItem | null>(null);
  const [deleteHoldTimer, setDeleteHoldTimer] = useState<NodeJS.Timeout | null>(null);
  const [isHoldingDelete, setIsHoldingDelete] = useState<number | null>(null);
  const [holdProgress, setHoldProgress] = useState<Record<number, number>>({});
  const [isRemoving, setIsRemoving] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSectionsList, setOriginalSectionsList] = useState<ISectionsList | null>(null);
  const [originalListName, setOriginalListName] = useState<string>('');
  const [isCreatingNewList, setIsCreatingNewList] = useState(false);
  const [newListData, setNewListData] = useState<ListItem | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const holdStartTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);

  // Reset delete state when switching tabs or views
  useEffect(() => {
    resetDeleteState();
  }, [activeTab, isExpanded]);

  const checkForChanges = useCallback(() => {
    if (!selectedListId) {
      setHasUnsavedChanges(false);
      return;
    }

    // For new lists, always consider them as having changes
    if (isCreatingNewList) {
      setHasUnsavedChanges(true);
      return;
    }

    if (!originalSectionsList) {
      setHasUnsavedChanges(false);
      return;
    }

    const selectedList = lists.find(list => list.id === selectedListId);
    if (!selectedList) {
      setHasUnsavedChanges(false);
      return;
    }

    // Check if name has changed (either in editing mode or saved)
    const nameChanged = isEditingListName 
      ? editingListName !== originalListName
      : selectedList.name !== originalListName;
    
    // Check if sections list has changed (deep comparison)
    const sectionsChanged = JSON.stringify(sectionsList) !== JSON.stringify(originalSectionsList);
    
    setHasUnsavedChanges(nameChanged || sectionsChanged);
  }, [originalSectionsList, originalListName, selectedListId, sectionsList, lists, isEditingListName, editingListName, isCreatingNewList]);

  // Check for changes whenever relevant state changes
  useEffect(() => {
    checkForChanges();
  }, [checkForChanges]);

  // Log sections list changes
  useEffect(() => {
    console.log('Sections List Updated:', JSON.stringify(sectionsList, null, 2));
    console.log('Selected Offers:', selectedOffers);
  }, [sectionsList, selectedOffers]);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await PanelService.getAllOffers(GroupOffersBy.PARTNERS, false, true);
        setPartners(Object.fromEntries(response.partners.map(p => [p.partnerId, p])));
        
        // Get all offers from byPartners object
        const allOffers = Object.values(response.byPartners || {}).flat();
        setSelectedOffers(allOffers);
        console.log('Fetched offers:', allOffers);
      } catch (error) {
        console.error('Error fetching partners:', error);
      }
    };

    const fetchSectionsLists = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const sectionsLists = await PanelService.getSectionsLists();
        
        // Convert API response to ListItem format and ensure proper structure
        const listItems: ListItem[] = sectionsLists.map((list: any) => ({
          id: list.id,
          name: list.name,
          sectionsList: {
            ...list,
            content: (list.content || []).map((section: any) => ({
              ...section,
              offerStatesOrdered: section.offerStatesOrdered || {}
            }))
          },
        }));
        
        setLists(listItems);
      } catch (error) {
        console.error('Error fetching sections lists:', error);
        setError('Failed to load sections lists');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPartners();
    fetchSectionsLists();
  }, []);

  const handleAddSection = () => {
    setIsAddSectionModalOpen(true);
  };

  const handleAddSectionSubmit = (sectionData: any) => {
    setSectionsList(prev => ({
      ...prev,
      content: [...prev.content, {
        sectionId: prev.content.length + 1,
        offerStatesOrdered: {}
      }]
    }));
    setIsAddSectionModalOpen(false);
  };

  const isOfferInAnySection = (offerId: number): boolean => {
    return sectionsList.content.some(section => 
      Object.values(section.offerStatesOrdered || {}).some(states =>
        (states || []).some(state => state.offerId === offerId)
      )
    );
  };

  const getOfferHiddenState = (offerId: number): boolean => {
    for (const section of sectionsList.content) {
      for (const states of Object.values(section.offerStatesOrdered || {})) {
        const state = (states || []).find(state => state.offerId === offerId);
        if (state) {
          return state.hidden;
        }
      }
    }
    return false;
  };

  const cleanupSection = (section: IAppOffersSection): IAppOffersSection => ({
    ...section,
    offerStatesOrdered: Object.fromEntries(
      Object.entries(section.offerStatesOrdered || {}).map(([key, states]) => [
        key,
        (states || []).filter(state => 
          state.offerId != null && selectedOffers.some(offer => offer.id === state.offerId)
        )
      ])
    )
  });

  const setSectionsListClean = (newState: ISectionsList | ((prev: ISectionsList) => ISectionsList)) => {
    if (typeof newState === 'function') {
      setSectionsList(prev => {
        const result = newState(prev);
        return {
          ...result,
          content: result.content.map(cleanupSection)
        };
      });
    } else {
      setSectionsList({
        ...newState,
        content: newState.content.map(cleanupSection)
      });
    }
  };

  const handleSectionExpand = (sectionId: number) => {
    if (sectionId !== expandedSection) {
      setExpandedSection(sectionId);
      onSectionsListChange?.(sectionsList);
    }
  };

  const handleSectionsListChange = (newSectionsList: ISectionsList) => {
    setSectionsList(newSectionsList);
    onSectionsListChange?.(newSectionsList);
    
    console.log('handleSectionsListChange', newSectionsList);
    // Update the selected list's sectionsList
    if (selectedListId) {
      setLists(prev => prev.map(list => 
        list.id === selectedListId 
          ? { ...list, sectionsList: newSectionsList }
          : list
      ));
    }
    
    // Trigger change detection immediately
    checkForChanges();
  };

  const handleDragStart = (offer: TOffer) => {
    if (!isReorderMode) {
      setDraggedOffer(offer);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    setDraggedOffer(null);
  };

  const handleAddOffer = (offerId: number, sectionId: number, insertIndex?: number) => {
    setSectionsList(prev => {
      return {
        ...prev,
        content: prev.content.map(section => {
          if (section.sectionId === sectionId) {
            const offer = selectedOffers.find(o => o.id === offerId);
            if (!offer) return section;
            
            const geo = offer.geo || 'other';
            const states = section.offerStatesOrdered[geo] || [];
            
            if (insertIndex !== undefined) {
              const newStates = [...states];
              newStates.splice(insertIndex, 0, { offerId, hidden: false });
              return {
                ...section,
                offerStatesOrdered: {
                  ...section.offerStatesOrdered,
                  [geo]: newStates
                }
              };
            } else {
              return {
                ...section,
                offerStatesOrdered: {
                  ...section.offerStatesOrdered,
                  [geo]: [...states, { offerId, hidden: false }]
                }
              };
            }
          }
          // Remove from other sections
          return {
            ...section,
            offerStatesOrdered: Object.fromEntries(
              Object.entries(section.offerStatesOrdered || {}).map(([key, states]) => [
                key,
                (states || []).filter(state => state.offerId !== offerId)
              ])
            )
          };
        })
      };
    });
  };

  const toggleReorderMode = () => {
    const newMode = !isReorderMode;
    if (draggedOffer) {
      setDraggedOffer(null);
    }
    
    if (newMode) {
      setLastExpandedSection(expandedSection);
      setExpandedSection(-1);
      setIsReorderMode(true);
    } else {
      // First expand the section
      handleSectionExpand(lastExpandedSection);
      // Then exit reorder mode in the next frame to allow animation
      requestAnimationFrame(() => {
        setIsReorderMode(false);
      });
    }
  };

  // New handlers for hierarchical view
  const handleCreateNewList = () => {
    const newList: ListItem = {
      id: Math.max(...lists.map(l => l.id), 0) + 1,
      name: `List ${lists.length + 1}`,
      sectionsList: {
        id: Math.max(...lists.map(l => l.sectionsList.id), 0) + 1,
        name: `List element ${lists.length + 1}`,
        content: [
          {
            sectionId: 1,
            offerStatesOrdered: {}
          }
        ]
      },
    };
    
    setNewListData(newList);
    setIsCreatingNewList(true);
    setSelectedListId(newList.id);
    setSectionsList(newList.sectionsList);
    setOriginalSectionsList(JSON.parse(JSON.stringify(newList.sectionsList))); // Deep copy
    setOriginalListName(newList.name);
    setIsExpanded(true);
    
    // Auto-start editing the name field
    setEditingListName(newList.name);
    setIsEditingListName(true);
    setHasUnsavedChanges(false);
  };

  const handleSelectList = (listId: number) => {
    const selectedList = lists.find(list => list.id === listId);
    if (selectedList) {
      setSelectedListId(listId);
      setIsExpanded(true);
      setSectionsList(selectedList.sectionsList);
      setOriginalSectionsList(JSON.parse(JSON.stringify(selectedList.sectionsList))); // Deep copy
      setOriginalListName(selectedList.name);
      setHasUnsavedChanges(false);
    }
  };

  const handleBackToList = () => {
    setIsExpanded(false);
    setSelectedListId(null);
    setIsEditingListName(false);
    
    // If we were creating a new list and didn't save it, clean up
    if (isCreatingNewList) {
      setIsCreatingNewList(false);
      setNewListData(null);
    }
  };

  const handleEditListName = () => {
    const selectedList = lists.find(list => list.id === selectedListId);
    if (selectedList) {
      setEditingListName(selectedList.name);
      setIsEditingListName(true);
    }
  };

  const handleSaveListName = () => {
    if (editingListName.trim() && selectedListId) {
      setLists(prev => prev.map(list => 
        list.id === selectedListId 
          ? { ...list, name: editingListName.trim() }
          : list
      ));
      setIsEditingListName(false);
      setEditingListName('');
      
      // Trigger change detection
      setTimeout(() => checkForChanges(), 0);
    }
  };

  const handleCancelEditListName = () => {
    setIsEditingListName(false);
    setEditingListName('');
  };

  const handleDeleteListStart = (list: ListItem) => {
    console.log('Starting delete hold for list:', list.name);
    setIsHoldingDelete(list.id);
    holdStartTimeRef.current = Date.now();
    
    holdTimeoutRef.current = setTimeout(() => {
      console.log('Hold timeout completed, showing modal for list:', list.name);
      setListToDelete(list);
      setIsDeleteModalOpen(true);
      // Don't reset holding state here, let the modal handle it
    }, 2000);

    // Animate progress with easing
    const animate = () => {
      if (isHoldingDelete !== list.id) return;

      const elapsed = Date.now() - holdStartTimeRef.current;
      const progress = Math.min(elapsed / 2000, 1);
      
      // Ease the progress for smoother animation
      const easedProgress = progress < 0.5
        ? 2 * progress * progress // Ease in
        : -1 + (4 - 2 * progress) * progress; // Ease out
      
      setHoldProgress({ [list.id]: easedProgress });

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const handleDeleteListCancel = () => {
    console.log('Cancelling delete hold');
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setIsHoldingDelete(null);
    setHoldProgress({});
  };

  const handleDeleteListConfirm = () => {
    console.log('Delete confirmed for list:', listToDelete?.name);
    if (listToDelete) {
      setLists(prev => prev.filter(list => list.id !== listToDelete.id));
      
      // If the deleted list was selected, clear selection
      if (selectedListId === listToDelete.id) {
        setSelectedListId(null);
        setIsExpanded(false);
      }
    }
    
    // Reset all states
    setIsDeleteModalOpen(false);
    setListToDelete(null);
    setIsHoldingDelete(null);
    setHoldProgress({});
    
    // Clear any remaining timers
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const handleDeleteListCancelModal = () => {
    console.log('Delete cancelled for list:', listToDelete?.name);
    // Reset all states
    setIsDeleteModalOpen(false);
    setListToDelete(null);
    setIsHoldingDelete(null);
    setHoldProgress({});
    
    // Clear any remaining timers
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const resetDeleteState = () => {
    setIsHoldingDelete(null);
    setHoldProgress({});
    setIsRemoving(null);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const handleSaveList = async () => {
    if (!selectedListId) return;
    
    try {
      setIsSaving(true);
      const selectedList = lists.find(list => list.id === selectedListId);
      const currentList = selectedList || newListData;
      
      if (!currentList) {
        console.error('Selected list not found');
        return;
      }
      
      // If currently editing the name, save the editing name
      let finalName = currentList.name;
      if (isEditingListName && editingListName.trim()) {
        finalName = editingListName.trim();
        // Exit edit mode
        setIsEditingListName(false);
        setEditingListName('');
      }
      
      const updatedSectionsList = {
        ...sectionsList,
        id: selectedListId,
        name: finalName
      };
      
      await PanelService.upsertSectionsList(updatedSectionsList);
      
      // If this was a new list, add it to the main lists state
      if (isCreatingNewList && newListData) {
        setLists(prev => [...prev, {
          ...newListData,
          name: finalName,
          sectionsList: updatedSectionsList
        }]);
        setIsCreatingNewList(false);
        setNewListData(null);
      } else {
        // Update existing list
        setLists(prev => prev.map(list => 
          list.id === selectedListId 
            ? { ...list, sectionsList: updatedSectionsList, name: finalName }
            : list
        ));
      }
      
      // Reset change tracking after successful save
      setOriginalSectionsList(JSON.parse(JSON.stringify(updatedSectionsList)));
      setOriginalListName(finalName);
      setHasUnsavedChanges(false);
      
      console.log('List saved successfully');
    } catch (error) {
      console.error('Error saving list:', error);
      // You might want to show a toast notification here
    } finally {
      setIsSaving(false);
    }
  };

  // Filter lists by search query
  const filteredLists = lists.filter(list => 
    list.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const particlesInit = useCallback(async (engine: Engine) => {
    await loadFull(engine);
  }, []);

  const particlesLoaded = useCallback(async (container: Container | undefined) => {
    if (container) {
      console.log("Particles container loaded");
    }
  }, []);

  // Render lists overview
  if (!isExpanded) {
    return (
      <div className="offers-container">
        <Particles
          id="tsparticles"
          init={particlesInit}
          loaded={particlesLoaded}
          options={{
            fullScreen: false,
            background: {
              color: {
                value: "#000000",
              },
            },
            fpsLimit: 60,
            interactivity: {
              events: {
                onHover: {
                  enable: true,
                  mode: ["trail", "repulse"],
                },
                resize: true,
              },
              modes: {
                trail: {
                  delay: 0.05,
                  pauseOnStop: true,
                  quantity: 2,
                  particles: {
                    size: {
                      value: { min: 2, max: 4 }
                    },
                    move: {
                      speed: 1.5,
                      outModes: {
                        default: "none"
                      }
                    },
                    opacity: {
                      value: 0.3,
                      animation: {
                        enable: true,
                        speed: 0.2,
                        minimumValue: 0,
                        sync: false,
                        destroy: "min"
                      }
                    }
                  }
                },
                repulse: {
                  distance: 100,
                  duration: 0.2,
                  factor: 10,
                  speed: 0.3,
                  maxSpeed: 1,
                  easing: "ease-out-cubic"
                }
              },
            },
            particles: {
              color: {
                value: "#169de0",
              },
              links: {
                enable: false,
              },
              move: {
                direction: "none",
                enable: true,
                outModes: {
                  default: "none",
                },
                random: true,
                speed: 0.2,
                straight: false,
              },
              number: {
                density: {
                  enable: true,
                  area: 800,
                },
                value: 100,
                limit: 200,
              },
              opacity: {
                value: 0.3,
                animation: {
                  enable: true,
                  speed: 0.2,
                  minimumValue: 0,
                  sync: false,
                  destroy: "min"
                }
              },
              shape: {
                type: "circle",
              },
              size: {
                value: { min: 2, max: 4 }
              },
              effect: {
                type: "star",
                options: {
                  star: {
                    blur: 3,
                    color: "#169de0",
                    frequency: 0.1,
                    opacity: 0.15,
                    size: 2
                  }
                }
              }
            },
            detectRetina: true,
          }}
        />
        <div className="lists-container">
          <div className="lists-header">
            <div className="lists-header-content">
              <div className="lists-tabs">
                <button 
                  className={`lists-tab ${activeTab === 'offerwall' ? 'active' : ''}`}
                  onClick={() => setActiveTab('offerwall')}
                >
                  Офервол
                </button>
                <button 
                  className={`lists-tab ${activeTab === 'direct' ? 'active' : ''}`}
                  onClick={() => setActiveTab('direct')}
                >
                  Прямі компанії
                </button>
                <button 
                  className={`lists-tab sections-tab ${activeTab === 'sections' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sections')}
                >
                  Секції
                </button>
              </div>
              <div className="lists-search" style={{ display: activeTab === 'sections' ? 'none' : 'flex' }}>
                <input
                  type="text"
                  placeholder="Пошук..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="lists-search-input"
                />
                <FontAwesomeIcon icon={faSearch} className="lists-search-icon" />
              </div>
            </div>
          </div>
          <div className="lists-content">
            {activeTab === 'offerwall' ? (
              <>
                <div className="lists-grid">
                  {filteredLists.map((list) => (
                    <div 
                      key={list.id}
                      className="list-card"
                      onClick={() => handleSelectList(list.id)}
                    >
                      <div className="list-card-content">
                        <div className="list-card-header">
                          <h3>{list.name}</h3>
                        </div>
                        <div className="list-card-info">
                          <span className="list-card-apps" style={{ textAlign: 'right', display: 'block' }}>Використовується в застосунках: 0</span>
                        </div>
                        <button 
                          className={`list-card-delete ${isHoldingDelete === list.id ? 'holding' : ''}`}
                          onMouseDown={() => handleDeleteListStart(list)}
                          onMouseUp={handleDeleteListCancel}
                          onMouseLeave={handleDeleteListCancel}
                          onTouchStart={() => handleDeleteListStart(list)}
                          onTouchEnd={handleDeleteListCancel}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FontAwesomeIcon 
                            icon={faTrash} 
                            className="trash-icon"
                          />
                          {isHoldingDelete === list.id && (
                            <div 
                              className="delete-progress"
                              style={{ width: `${holdProgress[list.id] * 100}%` }}
                            />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lists-footer">
                  <Button 
                    appearance="primary"
                    onClick={handleCreateNewList}
                    size="lg"
                  >
                    Новий список
                  </Button>
                </div>
              </>
            ) : activeTab === 'direct' ? (
              <div className="direct-companies-placeholder">
                <p>Тут будуть прямі компанії</p>
              </div>
            ) : null}
            {activeTab === 'sections' && (
              <SectionsManager />
            )}
          </div>
        </div>
        
        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && listToDelete && (
          <div className="delete-modal-overlay">
            <div className="delete-modal">
              <h3>Видалити список?</h3>
              <p>Ви дійсно хочете видалити список "{listToDelete.name}"?</p>
              
              {/* Check if list is used in apps */}
              <div className="delete-warning">
                <p>⚠️ Увага: Цей список використовується в 0 застосунках</p>
                <p>Всі компанії, які використовують цей список, будуть переведені на дефолтний список.</p>
              </div>
              
              <div className="delete-modal-buttons">
                <Button 
                  appearance="ghost"
                  onClick={handleDeleteListCancelModal}
                >
                  Скасувати
                </Button>
                <Button 
                  appearance="primary"
                  color="red"
                  onClick={handleDeleteListConfirm}
                >
                  Видалити
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render expanded offers list
  const selectedList = lists.find(list => list.id === selectedListId);

  return (
    <div className="offers-container">
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={{
          fullScreen: false,
          background: {
            color: {
              value: "#000000",
            },
          },
          fpsLimit: 60,
          interactivity: {
            events: {
              onHover: {
                enable: true,
                mode: ["trail", "repulse"],
              },
              resize: true,
            },
            modes: {
              trail: {
                delay: 0.05,
                pauseOnStop: true,
                quantity: 2,
                particles: {
                  size: {
                    value: { min: 2, max: 4 }
                  },
                  move: {
                    speed: 1.5,
                    outModes: {
                      default: "none"
                    }
                  },
                  opacity: {
                    value: 0.3,
                    animation: {
                      enable: true,
                      speed: 0.2,
                      minimumValue: 0,
                      sync: false,
                      destroy: "min"
                    }
                  }
                }
              },
              repulse: {
                distance: 100,
                duration: 0.2,
                factor: 10,
                speed: 0.3,
                maxSpeed: 1,
                easing: "ease-out-cubic"
              }
            },
          },
          particles: {
            color: {
              value: "#169de0",
            },
            links: {
              enable: false,
            },
            move: {
              direction: "none",
              enable: true,
              outModes: {
                default: "none",
              },
              random: true,
              speed: 0.2,
              straight: false,
            },
            number: {
              density: {
                enable: true,
                area: 800,
              },
              value: 100,
              limit: 200,
            },
            opacity: {
              value: 0.3,
              animation: {
                enable: true,
                speed: 0.2,
                minimumValue: 0,
                sync: false,
                destroy: "min"
              }
            },
            shape: {
              type: "circle",
            },
            size: {
              value: { min: 2, max: 4 }
            },
            effect: {
              type: "star",
              options: {
                star: {
                  blur: 3,
                  color: "#169de0",
                  frequency: 0.1,
                  opacity: 0.15,
                  size: 2
                }
              }
            }
          },
          detectRetina: true,
        }}
      />
      <div className="list-header">
        <div className="list-header-content">
          <Button 
            appearance="ghost"
            onClick={handleBackToList}
            style={{ marginRight: '16px' }}
          >
            ← Назад до списків
          </Button>
          <div className="list-header-info">
            {isEditingListName ? (
              <div className="list-name-edit">
                <Input
                  value={editingListName}
                  onChange={setEditingListName}
                  onPressEnter={handleSaveListName}
                  onBlur={handleSaveListName}
                  autoFocus
                  size="sm"
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(22, 157, 224, 0.5)',
                    color: '#ffffff',
                    fontSize: '18px',
                    fontWeight: '600',
                    minWidth: '200px'
                  }}
                />
                <Button 
                  appearance="ghost" 
                  size="xs"
                  onClick={handleCancelEditListName}
                  style={{ marginLeft: '8px', color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  ✕
                </Button>
              </div>
            ) : (
              <h2 
                onClick={handleEditListName}
                style={{ cursor: 'pointer' }}
                title="Натисніть для редагування"
              >
                {selectedList?.name || 'Невідомий список'}
              </h2>
            )}
          </div>
          <div className="list-header-actions" style={{ marginLeft: 'auto' }}>
            <Button 
              appearance="primary"
              color="blue"
              loading={isSaving}
              disabled={!hasUnsavedChanges}
              onClick={handleSaveList}
              style={{
                opacity: hasUnsavedChanges ? 1 : 0.6,
                cursor: hasUnsavedChanges ? 'pointer' : 'not-allowed'
              }}
            >
              {isSaving ? 'Збереження...' : 'Зберегти список'}
            </Button>
          </div>
        </div>
      </div>
      <div className="drag-drop-context">
        <DragDropContext 
          onDragEnd={handleDragEnd}
          enableDefaultSensors={true}
          dragHandleUsageInstructions=""
        >
          <SplitLayout
            leftPane={
              <div className="offers-drop-zone">
                <OffersList 
                  droppableId="offers-list" 
                  sectionsList={sectionsList}
                  allOffers={selectedOffers.reduce((acc, offer) => {
                    acc[offer.id] = offer;
                    return acc;
                  }, {} as Record<number, TOffer>)}
                  partners={partners}
                  defaultExpandedSection={expandedSection}
                  onSectionExpand={handleSectionExpand}
                  onSectionsListChange={handleSectionsListChange}
                  draggedOffer={draggedOffer}
                  onAddOffer={handleAddOffer}
                  isReorderMode={isReorderMode}
                  lastExpandedSection={lastExpandedSection}
                  onExitReorderMode={() => setIsReorderMode(false)}
                />
                <div className="list-buttons-container">
                  <Button 
                    appearance="ghost"
                    color={isReorderMode ? "green" : "yellow"}
                    style={{
                      fontWeight: isReorderMode ? 'bold' : 'normal'
                    }}
                    onClick={toggleReorderMode}
                  >
                    {isReorderMode ? 'Зберегти' : 'Змінити порядок'}
                  </Button>
                </div>
              </div>
            }
            rightPane={
              <AllOffersList 
                droppableId="all-offers-list" 
                sectionsList={sectionsList}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                getOfferHiddenState={getOfferHiddenState}
              />
            }
          />
        </DragDropContext>
      </div>
    </div>
  );
};

export default OfferListContainer; 